import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fileInput", "preview", "dropZone"]
  static values = {
    maxWidth: { type: Number, default: 1920 },
    maxHeight: { type: Number, default: 1920 },
    quality: { type: Number, default: 0.85 }
  }

  connect() {
    console.log('📷 image-upload controller connected')
    console.log('📷 dropZone target:', this.hasDropZoneTarget ? 'found' : 'NOT FOUND')
    this.setupDragAndDrop()
    this.optimizedFiles = []
  }

  async handleFiles(eventOrFiles) {
    // 파일 추출 (이벤트 또는 FileList 직접 전달 모두 지원)
    let files
    if (eventOrFiles instanceof FileList) {
      files = eventOrFiles
    } else if (eventOrFiles?.target?.files) {
      files = eventOrFiles.target.files
    } else if (eventOrFiles?.dataTransfer?.files) {
      files = eventOrFiles.dataTransfer.files
    } else if (eventOrFiles?.files) {
      files = eventOrFiles.files
    }

    console.log('📷 handleFiles called, files:', files?.length || 0)

    if (!files || files.length === 0) return

    if (files.length > 10) {
      alert('Chỉ được tải tối đa 10 ảnh')
      this.fileInputTarget.value = ''
      return
    }

    // 로딩 상태 표시
    this.showLoadingState(files.length)

    // 이미지 최적화
    this.optimizedFiles = []
    for (const file of Array.from(files)) {
      // HEIC 파일은 type이 비어있거나 image/heic일 수 있음
      const isImage = file.type.startsWith('image/') ||
                      file.name.toLowerCase().endsWith('.heic') ||
                      file.name.toLowerCase().endsWith('.heif')
      console.log('📷 processing:', file.name, 'type:', file.type, 'isImage:', isImage)
      if (!isImage) continue

      try {
        const optimized = await this.optimizeImage(file)
        this.optimizedFiles.push(optimized)
      } catch (error) {
        console.error('이미지 최적화 실패:', error)
        // 실패 시 원본 사용
        this.optimizedFiles.push(file)
      }
    }

    // 최적화된 파일을 file input에 할당
    const dt = new DataTransfer()
    this.optimizedFiles.forEach(file => dt.items.add(file))
    this.fileInputTarget.files = dt.files

    this.displayPreview(this.optimizedFiles)
  }

  showLoadingState(count) {
    this.previewTarget.innerHTML = ''
    this.previewTarget.classList.remove('hidden')

    const loading = document.createElement('div')
    loading.className = 'col-span-full text-center py-4'
    loading.innerHTML = `
      <div class="inline-flex items-center gap-2 text-gray-600">
        <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        <span>${count}장 이미지 최적화 중...</span>
      </div>
    `
    this.previewTarget.appendChild(loading)
  }

  async optimizeImage(file) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      img.onload = () => {
        // 원본 크기
        let { width, height } = img

        // 리사이즈 계산 (긴 변 기준)
        const maxDim = Math.max(this.maxWidthValue, this.maxHeightValue)
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width)
            width = maxDim
          } else {
            width = Math.round((width * maxDim) / height)
            height = maxDim
          }
        }

        canvas.width = width
        canvas.height = height

        // 흰색 배경 (HEIC 투명도 처리)
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(0, 0, width, height)

        // 이미지 그리기
        ctx.drawImage(img, 0, 0, width, height)

        // JPEG로 압축
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('이미지 변환 실패'))
              return
            }

            // 새 파일명 (확장자를 .jpg로)
            const originalName = file.name.replace(/\.[^/.]+$/, '')
            const newFileName = `${originalName}.jpg`

            const optimizedFile = new File([blob], newFileName, {
              type: 'image/jpeg',
              lastModified: Date.now()
            })

            // 최적화 결과 로그
            const savedPercent = Math.round((1 - blob.size / file.size) * 100)
            console.log(`📷 ${file.name}: ${this.formatBytes(file.size)} → ${this.formatBytes(blob.size)} (${savedPercent}% 절약)`)

            resolve(optimizedFile)
          },
          'image/jpeg',
          this.qualityValue
        )
      }

      img.onerror = () => reject(new Error('이미지 로드 실패'))

      // HEIC 등 특수 포맷도 지원
      const reader = new FileReader()
      reader.onload = (e) => {
        img.src = e.target.result
      }
      reader.onerror = () => reject(new Error('파일 읽기 실패'))
      reader.readAsDataURL(file)
    })
  }

  formatBytes(bytes) {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  displayPreview(files) {
    this.previewTarget.innerHTML = ''
    this.previewTarget.classList.remove('hidden')

    // 총 파일 크기 계산
    const totalSize = files.reduce((sum, f) => sum + f.size, 0)

    const header = document.createElement('div')
    header.className = 'col-span-full mb-2'
    header.innerHTML = `
      <p class="text-sm text-gray-600">
        <span class="font-medium">${files.length}</span> ảnh đã chọn
        <span class="text-gray-400 ml-2">(${this.formatBytes(totalSize)})</span>
        ${files.length >= 5 ? '<span class="text-green-600 ml-2">✓ Đủ ảnh cho bài mua bán</span>' : ''}
      </p>
    `
    this.previewTarget.appendChild(header)

    files.forEach((file, index) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const div = document.createElement('div')
        div.className = 'relative group'
        div.innerHTML = `
          <img src="${e.target.result}" class="w-full h-24 object-cover rounded-lg border-2 border-gray-200">
          <button type="button" data-action="click->image-upload#removeImage" data-index="${index}"
                  class="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-600">
            ✕
          </button>
          ${index === 0 ? '<span class="absolute bottom-1 left-1 bg-blue-500 text-white text-xs px-2 py-1 rounded">Ảnh đại diện</span>' : ''}
        `
        this.previewTarget.appendChild(div)
      }
      reader.readAsDataURL(file)
    })
  }

  removeImage(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index)

    this.optimizedFiles.splice(index, 1)

    const dt = new DataTransfer()
    this.optimizedFiles.forEach(file => dt.items.add(file))
    this.fileInputTarget.files = dt.files

    if (this.optimizedFiles.length === 0) {
      this.previewTarget.classList.add('hidden')
      this.previewTarget.innerHTML = ''
    } else {
      this.displayPreview(this.optimizedFiles)
    }
  }

  setupDragAndDrop() {
    if (!this.hasDropZoneTarget) {
      console.error('📷 dropZone target not found!')
      return
    }

    // 드래그 이벤트 등록
    const zone = this.dropZoneTarget

    zone.addEventListener('dragenter', (e) => {
      e.preventDefault()
      e.stopPropagation()
      console.log('📷 dragenter')
    })

    zone.addEventListener('dragover', (e) => {
      e.preventDefault()
      e.stopPropagation()
      zone.classList.add('border-blue-500', 'bg-blue-50')
    })

    zone.addEventListener('dragleave', (e) => {
      e.preventDefault()
      e.stopPropagation()
      zone.classList.remove('border-blue-500', 'bg-blue-50')
    })

    zone.addEventListener('drop', this.handleDrop.bind(this))

    console.log('📷 drag and drop events registered')
  }

  handleDragOver(e) {
    e.preventDefault()
    this.dropZoneTarget.classList.add('border-blue-500', 'bg-blue-50')
  }

  handleDragLeave(e) {
    e.preventDefault()
    this.dropZoneTarget.classList.remove('border-blue-500', 'bg-blue-50')
  }

  handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    console.log('📷 drop event fired')
    this.dropZoneTarget.classList.remove('border-blue-500', 'bg-blue-50')

    const files = e.dataTransfer.files
    console.log('📷 dropped files:', files.length, Array.from(files).map(f => `${f.name} (${f.type})`))

    if (files.length > 0) {
      this.handleFiles(files)
    }
  }
}
